// src/components/settings/PetPricesTab.tsx

import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  Backdrop,
  Box,
  IconButton,
  Typography,
  Alert,
  Table,
  TableRow,
} from '@mui/material';
import { Check as CheckIcon, Close as CloseIcon } from '@mui/icons-material';
import { getCustomIcon } from '../../utils/customIcons';
import { styled } from '@mui/material/styles';
import { referenceDataAPI, PetSize } from '../../services/api';
import { useErrorHandler } from '../../errors';
import { useGlobalToast } from '../../App';
import SettingsHeaderRow from './shared/SettingsHeaderRow';
import LoadingSpinner from '../common/LoadingSpinner';
import {
  StyledTableContainer,
  StyledTableHead,
  StyledTableHeaderCell,
  StyledTableBody,
  StyledTableRow,
  StyledTableCell,
} from './shared/SettingsTableElements';
import { InlineNumberInput } from '../forms/shared/FormElements';

type PriceField =
  | 'priceSingle'
  | 'priceSharing'
  | 'priceGroomingSmooth'
  | 'priceGroomingLong'
  | 'priceCut';

const PRICE_FIELD_LABELS: Record<PriceField, string> = {
  priceSingle: 'Single Rate',
  priceSharing: 'Sharing Rate',
  priceGroomingSmooth: 'Groom Smooth',
  priceGroomingLong: 'Groom Long',
  priceCut: 'Cut',
};

const PriceDisplay = styled(Typography)({
  cursor: 'pointer',
  width: '152px',
  height: '40px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  paddingRight: '36px',
  '&:hover': {
    textDecoration: 'underline dotted',
  },
});

const PriceSaveBtn = styled(IconButton)(({ theme }) => ({
  width: '56px',
  height: '56px',
  borderRadius: '4px',
  backgroundColor: theme.palette.success.main,
  color: '#fff',
  flexShrink: 0,
  '&:hover': {
    backgroundColor: theme.palette.success.dark,
  },
  '& svg': { fontSize: '2.5rem' },
}));

const PetPricesTab: React.FC = () => {
  const [items, setItems] = useState<PetSize[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingCell, setEditingCell] = useState<{
    id: number;
    field: PriceField;
  } | null>(null);
  const [editValue, setEditValue] = useState('');
  const [initialEditValue, setInitialEditValue] = useState('');
  const [heaterRate, setHeaterRate] = useState<number | null>(null);
  const [heaterEditing, setHeaterEditing] = useState(false);
  const [heaterEditValue, setHeaterEditValue] = useState('');
  const [heaterInitialValue, setHeaterInitialValue] = useState('');
  const { handleError } = useErrorHandler();
  const { showSuccess } = useGlobalToast();

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [sizes, systemSettings] = await Promise.all([
        referenceDataAPI.getPetSizes(),
        referenceDataAPI.getSystemSettings(),
      ]);
      setItems(sizes);
      setHeaterRate(systemSettings.heaterDailyRate ?? null);
    } catch (e) {
      handleError(e);
    } finally {
      setLoading(false);
    }
  }, [handleError]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatCurrency = (val?: number | null) =>
    val != null ? `R ${val.toFixed(2)}` : '-';

  const startEdit = (item: PetSize, field: PriceField) => {
    const current = item[field];
    const val = current != null ? String(current) : '';
    setEditValue(val);
    setInitialEditValue(val);
    setEditingCell({ id: item.id, field });
  };

  const cancelEdit = () => {
    setEditingCell(null);
    setEditValue('');
    setInitialEditValue('');
  };

  const commitEdit = async () => {
    if (!editingCell) return;
    const { id, field } = editingCell;
    const item = items.find((i) => i.id === id);
    if (!item) return;

    const newValue =
      editValue.trim() === '' ? undefined : parseFloat(editValue);
    const currentValue = item[field];

    // Close immediately so blur never races with a subsequent startEdit
    setEditingCell(null);
    setEditValue('');
    setInitialEditValue('');

    if (
      (newValue === undefined && currentValue == null) ||
      newValue === currentValue
    ) {
      return;
    }

    try {
      await referenceDataAPI.updatePetSize(item.id, {
        size: item.size,
        petTypeFk: item.petTypeFk,
        [field]: newValue,
      });
      setItems((prev) =>
        prev.map((i) => (i.id === id ? { ...i, [field]: newValue } : i))
      );
      showSuccess();
    } catch (e) {
      handleError(e);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') commitEdit();
    if (e.key === 'Escape') cancelEdit();
  };

  const clearField = () => setEditValue('');

  const resetField = () => setEditValue(initialEditValue);

  const startHeaterEdit = () => {
    const val = heaterRate != null ? String(heaterRate) : '';
    setHeaterEditValue(val);
    setHeaterInitialValue(val);
    setHeaterEditing(true);
  };

  const cancelHeaterEdit = () => {
    setHeaterEditing(false);
    setHeaterEditValue('');
    setHeaterInitialValue('');
  };

  const commitHeaterEdit = async () => {
    const newValue =
      heaterEditValue.trim() === '' ? null : parseFloat(heaterEditValue);
    cancelHeaterEdit();
    if (newValue === heaterRate) return;
    try {
      await referenceDataAPI.updateSystemSettings({
        heaterDailyRate: newValue ?? 0,
      });
      setHeaterRate(newValue);
      showSuccess();
    } catch (e) {
      handleError(e);
    }
  };

  const handleHeaterKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') commitHeaterEdit();
    if (e.key === 'Escape') cancelHeaterEdit();
  };

  const renderHeaterCell = () => (
    <Box sx={{ position: 'relative', display: 'inline-block' }}>
      <PriceDisplay
        onClick={() => !heaterEditing && startHeaterEdit()}
        sx={{
          visibility: heaterEditing ? 'hidden' : 'visible',
          fontSize: '1.8rem',
        }}
      >
        {formatCurrency(heaterRate)}
      </PriceDisplay>
      {heaterEditing &&
        createPortal(
          <Box
            sx={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1201,
              borderRadius: '4px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
              overflow: 'visible',
              minWidth: '260px',
            }}
          >
            <Box
              sx={(theme) => ({
                backgroundColor: theme.palette.primary.main,
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 12px',
                borderTopLeftRadius: '4px',
                borderTopRightRadius: '4px',
                userSelect: 'none',
              })}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  opacity: 0.9,
                  '& svg': { fontSize: '2.5rem' },
                }}
              >
                {getCustomIcon('heater', '2.5rem', '#fff')}
              </Box>
              <Typography
                sx={{ fontSize: '2rem', fontWeight: 600, color: '#fff' }}
              >
                Heaters
              </Typography>
            </Box>
            <IconButton
              onClick={cancelHeaterEdit}
              size="small"
              sx={(theme) => ({
                position: 'absolute',
                top: -4,
                right: -4,
                zIndex: 1,
                width: '2.7rem',
                height: '2.7rem',
                backgroundColor: theme.palette.primary.main,
                border: `3px solid ${theme.palette.primary.light}40`,
                boxShadow: theme.shadows[6],
                color: '#fff',
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                  border: `3px solid ${theme.palette.primary.light}`,
                },
                '& svg': { fontSize: '2rem' },
              })}
            >
              <CloseIcon />
            </IconButton>
            <Box
              sx={(theme) => ({
                backgroundColor: theme.palette.background.paper,
                borderBottomLeftRadius: '4px',
                borderBottomRightRadius: '4px',
                padding: '16px 24px 20px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '10px',
              })}
            >
              <Typography sx={{ fontWeight: 600, fontSize: '1.8rem' }}>
                Daily Rate
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '14px',
                  mt: '1rem',
                  mb: '.5rem',
                }}
              >
                <InlineNumberInput
                  autoFocus
                  value={heaterEditValue}
                  onChange={setHeaterEditValue}
                  onKeyDown={handleHeaterKeyDown}
                  step={5}
                  min={0}
                />
                <PriceSaveBtn
                  onClick={commitHeaterEdit}
                  aria-label="Save heater rate"
                >
                  <CheckIcon />
                </PriceSaveBtn>
              </Box>
              <Box sx={{ display: 'flex', gap: '36px' }}>
                <Typography
                  onClick={() => setHeaterEditValue('')}
                  sx={{
                    cursor: 'pointer',
                    fontSize: '2rem',
                    color: 'primary.main',
                    '&:hover': { color: 'secondary.main' },
                  }}
                >
                  Clear
                </Typography>
                <Typography
                  onClick={() => setHeaterEditValue(heaterInitialValue)}
                  sx={{
                    cursor: 'pointer',
                    fontSize: '2rem',
                    color: 'primary.main',
                    '&:hover': { color: 'secondary.main' },
                  }}
                >
                  Reset
                </Typography>
              </Box>
            </Box>
          </Box>,
          document.body
        )}
    </Box>
  );

  const renderPriceCell = (item: PetSize, field: PriceField) => {
    const isEditing =
      editingCell?.id === item.id && editingCell?.field === field;

    return (
      <Box sx={{ position: 'relative', display: 'inline-block' }}>
        <PriceDisplay
          onClick={() => !isEditing && startEdit(item, field)}
          sx={{ visibility: isEditing ? 'hidden' : 'visible' }}
        >
          {formatCurrency(item[field])}
        </PriceDisplay>
        {isEditing &&
          createPortal(
            <Box
              sx={{
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 1201,
                borderRadius: '4px',
                boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
                overflow: 'visible',
                minWidth: '260px',
              }}
            >
              <Box
                sx={(theme) => ({
                  backgroundColor: theme.palette.primary.main,
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 12px',
                  borderTopLeftRadius: '4px',
                  borderTopRightRadius: '4px',
                  userSelect: 'none',
                })}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    opacity: 0.9,
                    '& svg': { fontSize: '2.5rem' },
                  }}
                >
                  {getCustomIcon(item.petType?.type, '2.5rem', '#fff')}
                </Box>
                <Typography
                  sx={{ fontSize: '2rem', fontWeight: 600, color: '#fff' }}
                >
                  {item.petType?.type ?? ''}
                </Typography>
              </Box>

              <IconButton
                onClick={cancelEdit}
                size="small"
                sx={(theme) => ({
                  position: 'absolute',
                  top: -4,
                  right: -4,
                  zIndex: 1,
                  width: '2.7rem',
                  height: '2.7rem',
                  backgroundColor: theme.palette.primary.main,
                  border: `3px solid ${theme.palette.primary.light}40`,
                  boxShadow: theme.shadows[6],
                  color: '#fff',
                  '&:hover': {
                    backgroundColor: theme.palette.primary.dark,
                    border: `3px solid ${theme.palette.primary.light}`,
                  },
                  '& svg': { fontSize: '2rem' },
                })}
              >
                <CloseIcon />
              </IconButton>

              <Box
                sx={(theme) => ({
                  backgroundColor: theme.palette.background.paper,
                  borderBottomLeftRadius: '4px',
                  borderBottomRightRadius: '4px',
                  padding: '16px 24px 20px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '1px',
                })}
              >
                {item.petType?.type !== item.size && (
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: '1.8rem',
                      color: 'text.primary',
                    }}
                  >
                    {item.size}
                  </Typography>
                )}
                <Typography sx={{ fontWeight: 600, fontSize: '1.8rem' }}>
                  {PRICE_FIELD_LABELS[field]}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '14px',
                    mt: '1rem',
                    mb: '.5rem',
                  }}
                >
                  <InlineNumberInput
                    autoFocus
                    value={editValue}
                    onChange={setEditValue}
                    onKeyDown={handleKeyDown}
                    step={5}
                    min={0}
                  />
                  <PriceSaveBtn onClick={commitEdit} aria-label="Save price">
                    <CheckIcon />
                  </PriceSaveBtn>
                </Box>
                <Box sx={{ display: 'flex', gap: '36px' }}>
                  <Typography
                    onClick={clearField}
                    sx={{
                      cursor: 'pointer',
                      fontSize: '2rem',
                      color: 'primary.main',
                      '&:hover': { color: 'secondary.main' },
                    }}
                  >
                    Clear
                  </Typography>
                  <Typography
                    onClick={resetField}
                    sx={{
                      cursor: 'pointer',
                      fontSize: '2rem',
                      color: 'primary.main',
                      '&:hover': { color: 'secondary.main' },
                    }}
                  >
                    Reset
                  </Typography>
                </Box>
              </Box>
            </Box>,
            document.body
          )}
      </Box>
    );
  };

  if (loading) return <LoadingSpinner message="Loading Prices..." size={80} />;

  return (
    <Box>
      <Backdrop
        open={!!editingCell || heaterEditing}
        onClick={() => {
          cancelEdit();
          cancelHeaterEdit();
        }}
        sx={(theme) => ({
          zIndex: 1200,
          backgroundColor:
            theme.palette.mode === 'dark'
              ? 'rgba(0, 0, 0, 0.75)'
              : 'rgba(0, 0, 0, 0.8)',
          backdropFilter: 'blur(3px)',
        })}
      />
      <SettingsHeaderRow title="Prices" />
      {items.length === 0 ? (
        <Alert severity="info">
          No prices to display. Add sizes in the Sizes tab first.
        </Alert>
      ) : (
        <StyledTableContainer>
          <Table size="small">
            <StyledTableHead>
              <TableRow>
                <StyledTableHeaderCell>Type</StyledTableHeaderCell>
                <StyledTableHeaderCell>Size</StyledTableHeaderCell>
                <StyledTableHeaderCell>Single</StyledTableHeaderCell>
                <StyledTableHeaderCell>Sharing</StyledTableHeaderCell>
                <StyledTableHeaderCell>Groom Smooth</StyledTableHeaderCell>
                <StyledTableHeaderCell>Groom Long</StyledTableHeaderCell>
                <StyledTableHeaderCell>Cut</StyledTableHeaderCell>
              </TableRow>
            </StyledTableHead>
            <StyledTableBody>
              {items.map((item, index) => (
                <StyledTableRow key={item.id} isOdd={index % 2 === 0}>
                  <StyledTableCell>
                    <Typography>{item.petType?.type ?? '-'}</Typography>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Typography>{item.size}</Typography>
                  </StyledTableCell>
                  <StyledTableCell>
                    {renderPriceCell(item, 'priceSingle')}
                  </StyledTableCell>
                  <StyledTableCell>
                    {renderPriceCell(item, 'priceSharing')}
                  </StyledTableCell>
                  <StyledTableCell>
                    {renderPriceCell(item, 'priceGroomingSmooth')}
                  </StyledTableCell>
                  <StyledTableCell>
                    {renderPriceCell(item, 'priceGroomingLong')}
                  </StyledTableCell>
                  <StyledTableCell>
                    {renderPriceCell(item, 'priceCut')}
                  </StyledTableCell>
                </StyledTableRow>
              ))}
            </StyledTableBody>
          </Table>
        </StyledTableContainer>
      )}
      <SettingsHeaderRow title="Heaters" />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          padding: '4px 16px 16px',
          marginBottom: '3rem',
        }}
      >
        <Typography sx={{ fontSize: '1.8rem', fontWeight: 600 }}>
          Daily Rate
        </Typography>
        {renderHeaterCell()}
      </Box>
    </Box>
  );
};

export default PetPricesTab;
